import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Appearance, Platform } from 'react-native';

import { lightColors, darkColors, getColorsForMode } from '../utils/theme';
import type { ThemeMode, ColorTheme } from '../utils/theme';

/** localStorage / SecureStore key for persisted theme preference. */
const THEME_KEY = 'rinsepay_theme_mode';

interface ThemeContextType {
  readonly colors: ColorTheme;
  readonly themeMode: ThemeMode;
  readonly setThemeMode: (mode: ThemeMode) => void;
  readonly isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  colors: lightColors,
  themeMode: 'light',
  setThemeMode: () => {},
  isDark: false,
});

/** useTheme - access current color palette, theme mode, and dark-mode flag. */
export function useTheme(): ThemeContextType {
  return useContext(ThemeContext);
}

/** Loads persisted theme preference from platform storage. */
async function loadThemePreference(): Promise<ThemeMode> {
  if (Platform.OS === 'web') {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(THEME_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    }
    return 'light';
  }
  try {
    const store = await import('expo-secure-store');
    const stored = await store.getItemAsync(THEME_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // expo-secure-store unavailable (e.g. web fallback); default to light
  }
  return 'light';
}

/** Persists theme preference to platform storage. */
async function saveThemePreference(mode: ThemeMode): Promise<void> {
  if (Platform.OS === 'web') {
    if (typeof localStorage !== 'undefined') localStorage.setItem(THEME_KEY, mode);
    return;
  }
  try {
    const store = await import('expo-secure-store');
    await store.setItemAsync(THEME_KEY, mode);
  } catch {
    // expo-secure-store unavailable (e.g. web fallback); preference not persisted
  }
}

/** ThemeProvider - provides reactive theme colors to the component tree. */
export function ThemeProvider({ children }: { readonly children: React.ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>('light');
  const [systemDark, setSystemDark] = useState(() => Appearance.getColorScheme() === 'dark');

  useEffect(() => {
    loadThemePreference().then(setThemeModeState);
  }, []);

  useEffect(() => {
    const sub = Appearance.addChangeListener(({ colorScheme }) => {
      setSystemDark(colorScheme === 'dark');
    });
    return () => sub.remove();
  }, []);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    saveThemePreference(mode);
  }, []);

  const isDark = themeMode === 'dark' || (themeMode === 'system' && systemDark);
  const colors = useMemo(() => getColorsForMode(isDark), [isDark]);

  const value = useMemo(() => ({ colors, themeMode, setThemeMode, isDark }), [colors, themeMode, setThemeMode, isDark]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
